import React, { useState, useCallback, useEffect } from 'react';
import { MarketStatus } from '@sportspredict/shared';
import { directionToPosition } from '@/utils';

interface UseSwipeMarketProps {
	availableMarkets: any[];
	setUserTrades: React.Dispatch<React.SetStateAction<any[]>>;
	matchId?: string;
}

export const useSwipeMarket = ({
	availableMarkets,
	setUserTrades,
	matchId,
}: UseSwipeMarketProps) => {
	const [swipeStack, setSwipeStack] = useState<string[]>([]);
	const [versionById, setVersionById] = useState<Record<string, number>>({});
	const [swipeIntentById, setSwipeIntentById] = useState<Record<string, any>>({});
	const [isMarketCardExpanded, setIsMarketCardExpanded] = useState(false);

	useEffect(() => {
		setSwipeStack([]);
		setVersionById({});
		setSwipeIntentById({});
	}, [matchId]);

	useEffect(() => {
		const openMarketsIds = availableMarkets
			.filter((m) => m.status === MarketStatus.Open)
			.map((m) => m.id);

		setSwipeStack((prevStack) => {
			const syncedStack = prevStack.filter((id) => openMarketsIds.includes(id));
			const newMarketIds = openMarketsIds.filter((id) => !prevStack.includes(id));

			if (syncedStack.length === prevStack.length && newMarketIds.length === 0) {
				return prevStack;
			}

			return [...syncedStack, ...newMarketIds];
		});
	}, [availableMarkets]);

	const handleSwipeTrade = (marketId: string) => {
		setUserTrades((prev) => [...prev, { market_id: marketId }]);
		setSwipeStack((prev) => prev.filter((id) => id !== marketId));
		setIsMarketCardExpanded(false);
	};

	const moveToEnd = useCallback((marketId: string) => {
		setSwipeStack((prev) => {
			const idx = prev.indexOf(marketId);
			if (idx === -1) return prev;
			const newStack = [...prev];
			const [item] = newStack.splice(idx, 1);
			newStack.push(item);
			return newStack;
		});
	}, []);

	const bumpVersion = useCallback((marketId: string) => {
		setVersionById((prev) => ({
			...prev,
			[marketId]: (prev[marketId] ?? 0) + 1,
		}));
	}, []);

	const registerSwipe = useCallback(
		(dir: string, marketId: string) => {
			if (dir === 'down' || dir === 'up') {
				moveToEnd(marketId);
				bumpVersion(marketId);
				setSwipeIntentById((prev) => {
					if (!prev[marketId]) return prev;
					const copy = { ...prev };
					delete copy[marketId];
					return copy;
				});
				setIsMarketCardExpanded(false);
				return;
			}

			setSwipeIntentById((prev) => {
				const prevNonce = prev[marketId]?.nonce ?? 0;
				return {
					...prev,
					[marketId]: { dir, nonce: prevNonce + 1 },
				};
			});
		},
		[moveToEnd, bumpVersion]
	);

	return {
		swipeStack,
		versionById,
		swipeIntentById,
		isMarketCardExpanded,
		setIsMarketCardExpanded,
		handleSwipeTrade,
		moveToEnd,
		bumpVersion,
		registerSwipe,
		directionToPosition,
	};
};
